import React, { useState, useEffect } from 'react';
import EventCard from './EventCard';

/**
 * Today's Events Component
 * Date navigation with prev/next buttons
 * Filters events by selected date
 */
export default function TodaysEvents({ events, businesses, locations, analytics }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filteredEvents, setFilteredEvents] = useState([]);

  // Set time to midnight for date comparisons
  useEffect(() => {
    const date = new Date(currentDate);
    date.setHours(0, 0, 0, 0);
    setCurrentDate(date);
  }, []);

  useEffect(() => {
    // Format current date for comparison (YYYY-MM-DD)
    const targetDateStr = formatDateSQL(currentDate);

    // Filter events for the selected date
    const filtered = events.filter(event => {
      const eventDate = new Date(event.eventDate);
      const eventDateStr = formatDateSQL(eventDate);
      return eventDateStr === targetDateStr;
    });

    setFilteredEvents(filtered);
  }, [currentDate, events]);

  const formatDateSQL = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateShort = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) {
      return 'Today';
    } else if (date.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  const formatDateHeading = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) {
      return "Today's Events";
    } else if (date.getTime() === tomorrow.getTime()) {
      return "Tomorrow's Events";
    } else {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      return `Events for ${dayName}, ${monthDay}`;
    }
  };

  const goToPrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    if (analytics) {
      analytics.trackInteraction('todays_events_nav', { direction: 'prev', date: formatDateSQL(newDate) });
    }
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    if (analytics) {
      analytics.trackInteraction('todays_events_nav', { direction: 'next', date: formatDateSQL(newDate) });
    }
  };

  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return currentDate.getTime() === today.getTime();
  };

  return (
    <div>
      {/* Header with date navigation */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h2 className="text-3xl font-bold text-center md:text-left">
          {formatDateHeading(currentDate)}
        </h2>
        <div className="flex items-center justify-center md:justify-end gap-3">
          <button
            onClick={goToPrevDay}
            disabled={isToday()}
            className={`p-2 rounded-lg transition-colors ${isToday()
                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                : 'hover:bg-gray-100'
              }`}
            aria-label="Previous day"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
          </button>
          <div className="text-lg font-semibold text-gray-700 min-w-0 md:min-w-[140px] text-center">
            {formatDateShort(currentDate)}
          </div>
          <button
            onClick={goToNextDay}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Next day"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Events container */}
      <div className="min-h-[200px]">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No events scheduled for this day.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map(event => {
              const business = businesses.find(b => b.id === event.businessId);
              const location = locations.find(l => l.id === event.locationId);
              return (
                <EventCard
                  key={event.id}
                  event={event}
                  business={business}
                  location={location}
                  showDate={false}
                  analytics={analytics}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
